import { createContext, useContext, useReducer, useEffect } from 'react'
import { authService } from '../services/authService'

const AuthContext = createContext()

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true, error: null }
    case 'LOGIN_SUCCESS':
      return { ...state, loading: false, user: action.payload, error: null }
    case 'LOGIN_FAILURE':
      return { ...state, loading: false, user: null, error: action.payload }
    case 'LOGOUT':
      return { ...state, user: null, loading: false, error: null }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'CLEAR_ERROR':
      return { ...state, error: null }
    default:
      return state
  }
}

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    loading: true,
    error: null
  })

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token')
        if (token) {
          const userData = await authService.getCurrentUser()
          dispatch({ type: 'LOGIN_SUCCESS', payload: userData })
        } else {
          dispatch({ type: 'SET_LOADING', payload: false })
        }
      } catch (error) {
        localStorage.removeItem('token')
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    }

    checkAuth()
  }, [])

  const login = async (email, password) => {
    try {
      dispatch({ type: 'LOGIN_START' })
      const response = await authService.login(email, password)
      localStorage.setItem('token', response.token)
      dispatch({ type: 'LOGIN_SUCCESS', payload: response.data })
      return response
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE', payload: error.message })
      throw error
    }
  }

  const register = async (name, email, password) => {
    try {
      dispatch({ type: 'LOGIN_START' })
      const response = await authService.register(name, email, password)
      localStorage.setItem('token', response.token)
      dispatch({ type: 'LOGIN_SUCCESS', payload: response.data })
      return response
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE', payload: error.message })
      throw error
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    dispatch({ type: 'LOGOUT' })
  }

  const updateProfile = async (profileData) => {
    try {
      dispatch({ type: 'LOGIN_START' })
      const response = await authService.updateProfile(profileData)
      dispatch({ type: 'LOGIN_SUCCESS', payload: response })
      return response
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE', payload: error.message })
      throw error
    }
  }

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const value = {
    user: state.user,
    loading: state.loading,
    error: state.error,
    login,
    register,
    logout,
    updateProfile,
    clearError
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
